import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUnit } from "effector-react";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { useChatInboxViewModel } from "../viewmodel/useChatViewModel";
import {
  fetchConversationsRequested,
  loadMoreConversationsRequested,
  setConversationSearch,
  markConversationAsReadRequested,
} from "../store/chatStore";
import { ChatConversation } from "../types";
import { ChatPage } from "./ChatPage";
import { NewConversationModal } from "../components/NewConversationModal";
import styles from "./ChatLayout.module.scss";
import { $authStore } from "../../auth/store/auth";
import { SEO } from "@/core/components/SEO/SEO";

export const ChatLayout: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { conversations, loading, error, hasMore, unreadCount } =
    useChatInboxViewModel();
  const authStore = useUnit($authStore);
  const currentUserId = authStore.user?.uid;
  const [searchValue, setSearchValue] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNewConversation, setShowNewConversation] = useState(false);

  React.useEffect(() => {
    fetchConversationsRequested();
  }, []);

  const handleConversationClick = (conversation: ChatConversation) => {
    if (unreadCount > 0 && !conversation.is_read) {
      markConversationAsReadRequested(conversation.id.toString());
    }
    navigate(`/messages/${conversation.id}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setConversationSearch(val);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchValue("");
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setConversationSearch("");
  };

  const formatTimestamp = (conversation: ChatConversation): string => {
    try {
      if (conversation.last_message_date) {
        const ms = conversation.last_message_date * 1000;
        const date = new Date(ms);
        const diffHours = (Date.now() - ms) / 3_600_000;
        if (diffHours < 1) return "Just now";
        if (diffHours < 24)
          return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        if (diffHours < 48) return "Yesterday";
        const opts: Intl.DateTimeFormatOptions = {
          month: "short",
          day: "numeric",
        };
        if (date.getFullYear() !== new Date().getFullYear())
          opts.year = "numeric";
        return date.toLocaleDateString([], opts);
      }
      const raw = conversation.last_message?.created ?? conversation.updated;
      if (!raw) return "";
      const [datePart, timePart] = raw.split(" ");
      const [d, m, y] = datePart.split("-");
      const date = new Date(`${y}-${m}-${d}T${timePart}`);
      const diffHours = (Date.now() - date.getTime()) / 3_600_000;
      if (diffHours < 1) return "Just now";
      if (diffHours < 24)
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      if (diffHours < 48) return "Yesterday";
      const opts: Intl.DateTimeFormatOptions = {
        month: "short",
        day: "numeric",
      };
      if (date.getFullYear() !== new Date().getFullYear())
        opts.year = "numeric";
      return date.toLocaleDateString([], opts);
    } catch {
      return "";
    }
  };

  const isRead = (conversation: ChatConversation): boolean =>
    conversation.is_read;

  return (
    <div
      className={`${styles.chatLayout} ${conversationId ? styles.chatOpen : ""}`}
    >
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarHeaderTop}>
            <div className={styles.titleRow}>
              <h2>Messages</h2>
              {unreadCount > 0 && (
                <span className={styles.unreadBadge}>{unreadCount} unread</span>
              )}
            </div>
            <div className={styles.headerActions}>
              <button
                onClick={() => setShowNewConversation(true)}
                className={styles.refreshBtn}
                title="New conversation"
              >
                <i className="pi pi-pencil" />
              </button>
            </div>
          </div>
          <div className={styles.searchBar}>
            <i className="pi pi-search" />
            <input
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            {searchValue && (
              <button
                className={styles.searchClearBtn}
                onClick={handleSearchClear}
              >
                <i className="pi pi-times" />
              </button>
            )}
          </div>
        </div>

        <div className={styles.conversationsList}>
          {loading && conversations.length === 0 ? (
            <div className={styles.loadingContainer}>
              <ProgressSpinner style={{ width: "30px", height: "30px" }} />
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <i className="pi pi-exclamation-circle" />
              <p>{error}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className={styles.emptyContainer}>
              <i className="pi pi-inbox" />
              <p>No conversations yet</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const unread = !isRead(conversation);
              return (
                <div
                  key={conversation.id}
                  className={`${styles.conversationItem} ${
                    conversationId === conversation.id.toString()
                      ? styles.active
                      : ""
                  } ${unread ? styles.unread : ""}`}
                  onClick={() => handleConversationClick(conversation)}
                >
                  <div className={styles.avatar}>
                    {conversation.other_user_avatar ? (
                      <img
                        src={conversation.other_user_avatar}
                        alt={conversation.other_user_name || "User"}
                      />
                    ) : (
                      <div className={styles.avatarPlaceholder}>
                        <i className="pi pi-user" />
                      </div>
                    )}
                  </div>

                  <div className={styles.conversationContent}>
                    <div className={styles.conversationHeader}>
                      <div className={styles.userNameBlock}>
                        <span className={styles.userName}>
                          {conversation.other_user_name ||
                            `User ${conversation.other_uid}`}
                        </span>
                        {conversation.other_user_job && (
                          <span className={styles.userJob}>
                            {conversation.other_user_job}
                          </span>
                        )}
                      </div>
                      <span className={styles.timestamp}>
                        {formatTimestamp(conversation)}
                      </span>
                    </div>
                    <div className={styles.lastMessage}>
                      {conversation.last_message ? (
                        <span>
                          {conversation.last_message.source_uid ===
                          currentUserId
                            ? "You: "
                            : ""}
                          {conversation.last_message.message}
                        </span>
                      ) : (
                        <span className={styles.noMessages}>No messages</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {hasMore && (
            <button
              className={styles.loadMoreBtn}
              onClick={() => loadMoreConversationsRequested()}
              disabled={loading}
            >
              {loading ? "loading..." : "Load More"}
            </button>
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className={styles.chatArea}>
        {conversationId ? (
          <ChatPage />
        ) : (
          <>
            <SEO title={`Messages | EUcalls`} description={"Messages"} />
            <div className={styles.noChatSelected}>
              <i className="pi pi-comments" />
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the sidebar to start chatting</p>
            </div>
          </>
        )}
      </div>

      <NewConversationModal
        visible={showNewConversation}
        onHide={() => setShowNewConversation(false)}
      />
    </div>
  );
};
